import { buttons } from "./PDFButtons"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { zIndex, PDFPanelWidth, PDFBarHeight, rightBorderWidth } from "@nova/constants"
import { useCursorTracking } from "tools/events/useCursorProvider"
import { PDFPanelOption } from "./PDFPanelOption"
import { PDFTemplates } from "./PDFTemplates"
import { useLegendItems } from "../PDFLegend/LegendItems/useLegendItems"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectActiveMenu, selectColorTheme, setActiveMenu } from "../PDFSlice"
import * as Icons from "@nova/component-icons"
import classNames from "classnames"
import { useDefaultPDFMetadata } from "./useDefaultMetadata"
import { useResizableSidePanelWidth } from "panels/content/side-panel/SidePanel"
import { IconButton } from "@nova/component-btn"

const id = "pdfPanel"

export function PDFPanel({
    offsetX,
    offsetY,
    width,
    height,
}: {
    offsetX: number
    offsetY: number
    width: number
    height: number
}) {
    const dispatch = useAppDispatch()
    useCursorTracking(id)
    const theme = useAppSelector(selectColorTheme())
    useDefaultPDFMetadata()

    const {
        allLegendItems,
        toggle,
        enabledLegendItems,
        excludedLegendItems,
        maxLegendItems,
        hasExceededMax,
    } = useLegendItems({
        offsetX,
        offsetY,
        width,
        height,
    })

    const activeMenu = useAppSelector(selectActiveMenu())
    const activeButton = buttons.find((e) => e.id === activeMenu)

    const sidePanelWidth = useResizableSidePanelWidth()

    return (
        <div
            className={classNames("fixed flex pb-2 pointer-events-none", {
                dark: theme === "dark",
            })}
            style={{
                height: `calc(100% - ${PDFBarHeight}px)`,
                width: `calc(100% - ${sidePanelWidth}px - ${rightBorderWidth}px)`,
                top: PDFBarHeight,
                zIndex: zIndex.PDFSnapshot,
            }}
        >
            <div
                className="border-[1px] border-white-200 shadow-[0_0_0_8px_rgb(18,18,22)] rounded-lg mr-2"
                style={{
                    height: `calc(100%)`,
                    width: `calc(100% - ${PDFPanelWidth}px)`,
                }}
            ></div>
            <ScrollableArea
                className={classNames(
                    "bg-gray-800 px-3 pb-4 border border-white-200 flex flex-col shadow-[0_0_0_8px_rgb(18,18,22)] pointer-events-auto h-full",
                    { dark: theme === "dark" }
                )}
                style={{
                    width: PDFPanelWidth,
                    zIndex: zIndex.PDFSnapshot,
                }}
                id={id}
            >
                {activeButton?.Menu ? (
                    <div className="text-gray-100 flex flex-col">
                        <div className="sticky bg-gray-800 pt-4 top-0 z-10">
                            <IconButton
                                className="flex items-center gap-2 cursor-pointer"
                                onClick={() => dispatch(setActiveMenu(undefined))}
                            >
                                <Icons.Icon Icon={Icons.ArrowLeft} className="text-blue-500" />
                                <h5 className="text-gray-100">Back</h5>
                            </IconButton>
                            <div className="flex items-center gap-2 pb-2 pt-4 border-b border-b-gray-700">
                                <h4 className="text-gray-0">{activeButton.label}</h4>
                            </div>
                        </div>
                        <activeButton.Menu
                            legendItems={allLegendItems}
                            enabledLegendItems={enabledLegendItems}
                            excludedLegendItems={excludedLegendItems}
                            toggle={toggle}
                            maxLegendItems={maxLegendItems}
                            hasExceededMax={hasExceededMax}
                        />
                    </div>
                ) : (
                    <>
                        <h4 className="text-gray-200 mb-2 pt-4">Elements</h4>
                        {buttons.map(
                            (
                                {
                                    Icon,
                                    id,
                                    label,
                                    Menu,
                                    excludedVisibilityToggle,
                                    showIconOnButton,
                                    onToggle,
                                },
                                idx
                            ) => {
                                return (
                                    <PDFPanelOption
                                        id={id}
                                        icon={Icon}
                                        label={label}
                                        hasMenu={!!Menu}
                                        excludedVisibilityToggle={excludedVisibilityToggle}
                                        showIconOnButton={showIconOnButton}
                                        onToggle={onToggle}
                                        key={idx}
                                    />
                                )
                            }
                        )}
                        <PDFTemplates />
                    </>
                )}
            </ScrollableArea>
        </div>
    )
}
